import protocoloApi from '../../../services/protocoloApi';
import { authService } from '../../../services';
import logger from '../../../utils/logger';

// Situacao vem da API como código numérico (string). Valores confirmados com o
// time de negócio; 2, 5, 6, 7, 8 e 10 ainda faltam confirmar.
const SITUACAO_REQUERIMENTO = {
  '1': 'Enviado',
  '3': 'Aguardando Pagamento',
  '4': 'Pendente',
  '9': 'Cancelado'
};

// Cor do badge de status na UI (não vem da API, é só apresentação).
const SITUACAO_COR = {
  '1': '#2e7d32',
  '3': '#f9a825',
  '4': '#616161',
  '9': '#c62828'
};

const formatarData = (isoDate) => {
  if (!isoDate) return '—';
  const data = new Date(isoDate);
  return Number.isNaN(data.getTime()) ? '—' : data.toLocaleDateString('pt-BR');
};

const formatarDataHora = (isoDate) => {
  if (!isoDate) return null;
  const data = new Date(isoDate);
  if (Number.isNaN(data.getTime())) return null;

  return data.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Os 3 status possíveis de um protocolo ainda em andamento (aba "Em Andamento"
// da Consulta de Protocolos) — única fonte de verdade tanto pra resolver o
// status de cada protocolo (resolverStatusAndamento) quanto pra legenda
// explicativa exibida pro usuário (StatusLegend).
export const STATUS_ANDAMENTO = [
  {
    label: 'Aguardando Tramitação',
    cor: '#616161',
    descricao: 'Ainda não foi encaminhado pra nenhum departamento.'
  },
  {
    label: 'Em Trâmite',
    cor: '#f9a825',
    descricao: 'Já foi encaminhado, mas ainda não foi recebido no destino.'
  },
  {
    label: 'Recebido no Destino',
    cor: '#1565c0',
    descricao: 'Já foi encaminhado e recebido no departamento de destino.'
  }
];

// Status de andamento do DocumentoProtocolo (seção 3.3 da API) — não vem pronto
// da API, é derivado aqui a partir de concluido/tramitado/ultimoTramite.
const resolverStatusAndamento = ({ concluido, tramitado, ultimoTramite }) => {
  if (concluido) {
    return { label: 'Concluído', cor: '#2e7d32' };
  }
  if (!tramitado) {
    return STATUS_ANDAMENTO[0];
  }
  if (!ultimoTramite?.dataRecebimento) {
    return STATUS_ANDAMENTO[1];
  }
  return STATUS_ANDAMENTO[2];
};

// Regras de negócio fixas do Protocolo Online desta OAB (definidas pelo time de negócio).
// Exportadas porque quem só tem o modeloRequerimento (ex: pendenciasService,
// resolvendo pendência de documento indeferido) precisa reconhecer que 221/258
// são do Protocolo Online, não de um pedido do catálogo de departamentos.
export const MODELO_REQUERIMENTO_INSCRITO = '221';
export const MODELO_REQUERIMENTO_NAO_INSCRITO = '258';
export const TIPOS_ASSUNTO_PROTOCOLO_ONLINE = '430';
const DEPARTAMENTO_DESTINO_PROTOCOLO_ONLINE = '83';
const TIPO_PEDIDO_PROTOCOLO_ONLINE = '92';

/**
 * ProtocoloService - Serviço para gerenciar protocolos online
 *
 * Responsabilidades:
 * - Criar protocolo (rascunho ou já finalizado, dependendo da situacao)
 * - Finalizar protocolo
 * - Atualizar só a situação de um requerimento, sem finalizar
 * - Buscar status de protocolo
 * - Listar protocolos de uma pessoa
 */
class ProtocoloService {

  /**
   * Extrai pessoaId/usuarioId do token JWT, com fallback pros dados do usuário
   * vindos do login (localStorage).
   * @param {Object} userData - Dados do usuário (fallback se o token falhar)
   */
  extrairIdsUsuario(userData) {
    let pessoaId = null;
    let usuarioId = null;

    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        pessoaId = payload.pessoaId;
        usuarioId = payload.userId;
      } catch (e) {
        // token inválido, cai no fallback abaixo
      }
    }

    if (!pessoaId && userData) {
      pessoaId = userData.pessoaId || userData.PessoaID;
    }
    if (!usuarioId && userData) {
      usuarioId = userData.id || userData.ID || userData.userId;
    }

    if (!pessoaId) {
      throw new Error('Não foi possível encontrar o ID da pessoa');
    }
    if (!usuarioId) {
      throw new Error('Não foi possível encontrar o ID do usuário');
    }

    return { pessoaId, usuarioId };
  }

  /**
   * Cria um novo protocolo (rascunho, ou já finalizado se situacao === 1)
   * @param {Object} dados - Dados do protocolo
   * @returns {Promise} Resposta da API
   */
  async criarProtocolo(dados) {
    try {
      const response = await protocoloApi.post('/protocolo/criar', dados);
      return response.data;
    } catch (error) {
      logger.error('Erro ao criar protocolo:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Finaliza um rascunho de protocolo (com dados de inscrição/pagamento)
   * @param {string} solicitacaoServicoId - ID da solicitação/rascunho
   * @param {Object} dados - Dados complementares (pessoaId, usuarioId, tiposAssunto, departamentoDestino)
   * @returns {Promise} Resposta da API
   */
  async finalizarProtocolo(solicitacaoServicoId, dados) {
    if (!solicitacaoServicoId) {
      throw new Error('solicitacaoServicoId é obrigatório');
    }

    try {
      const response = await protocoloApi.put(`/protocolo/finalizar/${solicitacaoServicoId}`, dados);
      return response.data;
    } catch (error) {
      logger.error('Erro ao finalizar protocolo:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Troca só a situação do requerimento, sem finalizar o protocolo.
   * @param {string} requerimentoId - ID do requerimento
   * @param {number} situacao - Novo código de situação
   * @returns {Promise} Resposta da API
   */
  async atualizarStatusRequerimento(requerimentoId, situacao) {
    if (!requerimentoId) {
      throw new Error('requerimentoId é obrigatório');
    }

    try {
      const response = await protocoloApi.put(`/protocolo/requerimento/${requerimentoId}/status`, { situacao });
      return response.data;
    } catch (error) {
      logger.error('Erro ao atualizar status do requerimento:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Busca o status de um protocolo (rascunho ou finalizado)
   * @param {string} solicitacaoServicoId - ID da solicitação/rascunho
   * @returns {Promise} Status do protocolo
   */
  async buscarStatusProtocolo(solicitacaoServicoId) {
    try {
      const response = await protocoloApi.get(`/protocolo/status/${solicitacaoServicoId}`);
      return response.data;
    } catch (error) {
      logger.error('Erro ao buscar status do protocolo:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Lista os campos configurados pra um modeloRequerimento (ModeloRequerimentoCampo),
   * usado pra descobrir dinamicamente o modeloRequerimentoCampoId de cada campo antes
   * de gravar em DadosRequerimento (ver salvarDadosRequerimento).
   * @param {string|number} modeloRequerimentoId - ID do modeloRequerimento
   * @returns {Promise} Resposta da API com a lista de campos
   */
  async listarCamposModeloRequerimento(modeloRequerimentoId) {
    try {
      const response = await protocoloApi.get(`/protocolo/modelo-requerimento/${modeloRequerimentoId}/campos`);
      return response.data;
    } catch (error) {
      logger.error('Erro ao listar campos do modeloRequerimento:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Grava os campos do formulário de um requerimento em DadosRequerimento.
   * @param {string} requerimentoId - ID do requerimento
   * @param {Object} dados - { usuarioId, campos: [{ modeloRequerimentoCampoId, valor }] }
   * @returns {Promise} Resposta da API
   */
  async salvarDadosRequerimento(requerimentoId, dados) {
    if (!requerimentoId) {
      throw new Error('requerimentoId é obrigatório');
    }

    try {
      const response = await protocoloApi.post(`/protocolo/requerimento/${requerimentoId}/dados`, dados);
      return response.data;
    } catch (error) {
      logger.error('Erro ao salvar dados do requerimento:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Lista todos os requerimentos/pedidos de uma pessoa.
   * Extrai os campos de exibição (SolicitacaoServico, ModeloRequerimento,
   * dataCriacao, Situacao) e, pra continuar/descartar rascunhos em Minhas
   * Solicitações, requerimentoId + documentoId (null enquanto é rascunho).
   * @param {string} pessoaId - ID da pessoa
   * @returns {Promise<Array>} Lista de requerimentos já mapeados para a UI
   */
  async listarProtocolosPorPessoa(pessoaId) {
    if (!pessoaId) {
      throw new Error('pessoaId é obrigatório');
    }

    try {
      const response = await protocoloApi.get(`/protocolo/listar/${pessoaId}`);
      const registros = response.data?.data || [];

      return registros.map((item) => {
        const situacaoCodigo = String(item.Situacao);

        return {
          requerimentoId: item.requerimentoId ?? null,
          documentoId: item.documentoId ?? null,
          situacaoCodigo,
          solicitacaoServico: item.SolicitacaoServico ?? '—',
          modeloRequerimento: item.ModeloRequerimento ?? 'Modelo não identificado',
          dataCriacao: formatarData(item.dataCriacao),
          situacao: SITUACAO_REQUERIMENTO[situacaoCodigo] ?? 'Situação desconhecida',
          situacaoCor: SITUACAO_COR[situacaoCodigo] ?? '#6c757d'
        };
      });
    } catch (error) {
      logger.error('Erro ao listar protocolos:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Lista os DocumentoProtocolo (protocolos já finalizados) de uma pessoa, com
   * o andamento de tramitação de cada um (GET /protocolo/documentos-protocolo/:pessoaId).
   * Diferente de listarProtocolosPorPessoa: aqui só entram protocolos finais
   * (rascunho não aparece), mas cada um já vem com o status de tramitação
   * resolvido pra exibição (ver resolverStatusAndamento).
   * @param {string} pessoaId - ID da pessoa
   * @returns {Promise<Array>} Lista de protocolos já mapeados para a UI
   */
  async listarDocumentosProtocoloPorPessoa(pessoaId) {
    if (!pessoaId) {
      throw new Error('pessoaId é obrigatório');
    }

    try {
      const response = await protocoloApi.get(`/protocolo/documentos-protocolo/${pessoaId}`);
      const registros = response.data?.data || [];

      return registros.map((item) => {
        const status = resolverStatusAndamento(item);

        return {
          id: item.id,
          numeroProtocolo: item.protocolo ?? '—',
          tipoPedido: item.tipoPedido ?? 'Pedido não identificado',
          dataProtocolo: formatarData(item.dataProtocolo),
          departamentoOrigem: item.departamentoOrigem ?? '—',
          departamentoDestino: item.departamentoDestino ?? '—',
          concluido: Boolean(item.concluido),
          tramitado: Boolean(item.tramitado),
          ultimoTramiteData: item.ultimoTramite ? formatarDataHora(item.ultimoTramite.dataEncaminhado) : null,
          ultimoTramiteRecebimento: item.ultimoTramite ? formatarDataHora(item.ultimoTramite.dataRecebimento) : null,
          statusLabel: status.label,
          statusCor: status.cor
        };
      });
    } catch (error) {
      logger.error('Erro ao listar documentos de protocolo:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Cria o protocolo do fluxo "Protocolo Online". modeloRequerimento muda conforme
   * a pessoa ser inscrita ou não na OAB (isInscrito vem dos dados da OAB salvos
   * no login — usado só aqui no front, não vai no body).
   *
   * Cria só o Requerimento, como rascunho (situacao 4 = Pendente) — o Protocolo/
   * DocumentoProtocolo final só é criado quando a pessoa concluir o envio dos
   * documentos (finalizarProtocoloOnline), pra nunca sobrar um protocolo sem anexo.
   * @param {Object} userData - Dados do usuário logado (fallback se o token falhar)
   * @returns {Promise} Resposta da API com solicitacaoId, requerimentoId, etc.
   */
  async criarProtocoloOnline(userData) {
    const { pessoaId, usuarioId } = this.extrairIdsUsuario(userData);

    const oabData = authService.getOABData();
    const isInscrito = oabData?.isInscrito || false;

    const dados = {
      pessoaId: Number(pessoaId),
      usuarioId: Number(usuarioId),
      situacao: 4,
      modeloRequerimento: isInscrito ? MODELO_REQUERIMENTO_INSCRITO : MODELO_REQUERIMENTO_NAO_INSCRITO,
      tiposAssunto: TIPOS_ASSUNTO_PROTOCOLO_ONLINE,
      // tipoPedido só é exigido pela API quando situacao=1 (aqui é sempre 4/Pendente),
      // mas mandamos assim mesmo pra já deixar populado.
      tipoPedido: TIPO_PEDIDO_PROTOCOLO_ONLINE,
      registroConselho: oabData?.registroConselho,
      // resolvido no login a partir de oab.tipoInscricaoAtual (ver authService.js);
      // undefined quando a pessoa não é inscrita, e aí o axios omite a chave.
      tipoInscricaoRemetente: oabData?.tipoInscricaoRemetente
    };

    return this.criarProtocolo(dados);
  }

  /**
   * Finaliza o rascunho do Protocolo Online: transforma o Requerimento em
   * Protocolo/DocumentoProtocolo final. Chamado só depois que a pessoa concluiu
   * a etapa de documentos (o anexo em si vai pra documentoAnexoService, numa API separada).
   *
   * A API de Anexo de GEDs pede, no campo "protocoloId", o ID da linha em
   * DocumentoProtocolo — não o Protocolo.ID que /finalizar devolve como "protocoloId".
   * Por isso, depois de finalizar, busca o status pra pegar requerimento.DocumentoProtocolo
   * e já devolve isso pronto como "documentoProtocoloId".
   * @param {string} solicitacaoServicoId - solicitacaoId retornado por criarProtocoloOnline
   * @param {Object} userData - Dados do usuário logado (fallback se o token falhar)
   * @returns {Promise} Resposta da API com protocoloId, numeroProtocolo, documentoProtocoloId, etc.
   */
  async finalizarProtocoloOnline(solicitacaoServicoId, userData) {
    const { pessoaId, usuarioId } = this.extrairIdsUsuario(userData);
    const oabData = authService.getOABData();

    const dados = {
      pessoaId: Number(pessoaId),
      usuarioId: Number(usuarioId),
      tiposAssunto: TIPOS_ASSUNTO_PROTOCOLO_ONLINE,
      departamentoDestino: DEPARTAMENTO_DESTINO_PROTOCOLO_ONLINE,
      // tipoPedido é sempre obrigatório em /finalizar.
      tipoPedido: TIPO_PEDIDO_PROTOCOLO_ONLINE,
      registroConselho: oabData?.registroConselho,
      tipoInscricaoRemetente: oabData?.tipoInscricaoRemetente
    };

    const finalizeResponse = await this.finalizarProtocolo(solicitacaoServicoId, dados);
    const finalizeResult = finalizeResponse.data || finalizeResponse;

    const statusResponse = await this.buscarStatusProtocolo(solicitacaoServicoId);
    const status = statusResponse.data || statusResponse;
    const documentoProtocoloId = status?.requerimento?.DocumentoProtocolo || null;

    return {
      ...finalizeResponse,
      data: {
        ...finalizeResult,
        documentoProtocoloId
      }
    };
  }
}

const protocoloService = new ProtocoloService();
export default protocoloService;
